import React from 'react';
import {Link} from 'react-router-dom';

import isEmpty from 'lodash.isempty';
import {CopyButton} from 'src/components/copy-button/copy-button';
import {DataType} from 'src/components/data-types/data-type';
import {PageHeadline} from 'src/components/page-headline/page-headline';
import {PossibleValues} from 'src/components/possible-values/possible-values';
import {ColumnTypes, ColumnWidth} from 'src/modules/attribute/constants';
import {AttributeExcelDownload} from 'src/modules/attribute/download/attribute-excel-download';
import {createEventRoutePath} from 'src/modules/event/event-route-planner';
import {AttributeDetailInfo} from 'src/openapi/models/AttributeDetailInfo';
import {User} from 'src/openapi/models/User';
import {getPossibleValuesOrDefault} from 'src/utils/getPossibleValuesOrDefault';

import {CheckBox} from '@pb/kit-component-checkbox';
import {Dropdown} from '@pb/kit-component-dropdown-v2';
import EllipsisTextHint from '@pb/kit-component-ellipsis-text-hint';
import {Group, Item} from '@pb/kit-component-group';
import {InfoIconButton} from '@pb/kit-component-icon-button';
import Loading from '@pb/kit-component-loading';
import Table, {Body, Cell, Head, Row} from '@pb/kit-component-table';
import {formatDate} from '@pb/kit-formatters';

import {eventDeprecated} from './attribute.module.styl';

type Props = {
    attributeName: string,
    attributes: Array<AttributeDetailInfo>,
    allAttributes: Array<AttributeDetailInfo>,
    intersectRef: (node: Element | null) => void,
    isVisible: boolean,
    hasMore: boolean,
    isSelectedEventCode: boolean,
    user: User | null,
};

const AMOUNT_LEFT_TO_UPLOAD_NEXT = 5;
const NO_ATTRIBUTE_USAGE = 'No Usage';

export const AttributeView = (props: Props) => {
    const {
        attributes,
        allAttributes,
        attributeName,
        intersectRef,
        isVisible,
        hasMore,
        isSelectedEventCode,
        user,
    } = props;

    return (
        <>
            <PageHeadline caption={`Attribute | ${attributeName}`} captionToCopy={attributeName}>
                <AttributeExcelDownload
                    attributeName={attributeName}
                    attributes={allAttributes}
                    user={user}
                />
            </PageHeadline>
            {!isEmpty(attributes) && (
                <Group direction="v" gap="l">
                    <Item>
                        <strong>
                            Usages in other event codes:
                        </strong>
                    </Item>
                    <Item>
                        <Table borderedGrid={true}>
                            <Head>
                                <Cell width={ColumnWidth[ColumnTypes.EVENT_CODE]}>Event Code</Cell>
                                <Cell width={ColumnWidth[ColumnTypes.EVENT_TYPE]}>Event Type</Cell>
                                <Cell width={ColumnWidth[ColumnTypes.EVENT_SOURCE]}>Event Source</Cell>
                                <Cell width={ColumnWidth[ColumnTypes.ATTRIBUTE_DESCRIPTION]}>
                                    Attribute Description
                                </Cell>
                                <Cell width={ColumnWidth[ColumnTypes.DATA_TYPE]}>Data Type</Cell>
                                <Cell width={ColumnWidth[ColumnTypes.POSSIBLE_VALUES]}>Possible Values</Cell>
                                <Cell width={ColumnWidth[ColumnTypes.ATTRIBUTE_DEPRECATED]}>Attribute Deprecated</Cell>
                                <Cell width={ColumnWidth[ColumnTypes.ATTRIBUTE_LAST_USAGE_DATE]}>
                                    Last Usage Date
                                </Cell>
                            </Head>
                            <Body>
                                {attributes.map((attribute, i, arr) => (
                                    <Row
                                        key={attribute.id}
                                        highlighted={i === 0 && isSelectedEventCode}
                                        ref={node => (i === arr.length - AMOUNT_LEFT_TO_UPLOAD_NEXT ?
                                                intersectRef(node as Element) : undefined)}>
                                        <Cell title={true}>
                                            <Item style={{maxWidth: '100%'}}>
                                                {attribute.eventDeprecated ? (
                                                    <Group justify="space-between" alignItems="center" gap="xs">
                                                        <EllipsisTextHint>
                                                            <Link
                                                                className={eventDeprecated}
                                                                to={createEventRoutePath({
                                                                    eventId: attribute.eventId ?? 0,
                                                                })}>
                                                                <EllipsisTextHint>
                                                                    {attribute.eventCode}
                                                                </EllipsisTextHint>
                                                            </Link>
                                                        </EllipsisTextHint>
                                                        <Item>
                                                            <Dropdown
                                                                hoverEnabled={true}
                                                                clickEnabled={false}
                                                                maxWidth={500}
                                                                anchor={({anchorRef, getAnchorProps}) => (
                                                                    <InfoIconButton
                                                                        {...getAnchorProps()}
                                                                        ref={anchorRef}
                                                                    />
                                                                )}>
                                                                Event is deprecated
                                                            </Dropdown>
                                                        </Item>
                                                    </Group>
                                                ) : (
                                                    <Link
                                                        to={createEventRoutePath({eventId: attribute.eventId ?? 0})}>
                                                        <EllipsisTextHint>{attribute.eventCode}</EllipsisTextHint>
                                                    </Link>
                                                )}
                                            </Item>
                                        </Cell>
                                        <Cell title={true}>
                                            {attribute.eventType}
                                        </Cell>
                                        <Cell title={true}>
                                            {attribute.source}
                                        </Cell>
                                        <Cell title={true}>
                                            {attribute.description}
                                        </Cell>
                                        <Cell>
                                            <DataType
                                                dataType={attribute.fieldType}
                                                itemsType={attribute?.itemsType}
                                            />
                                        </Cell>
                                        <Cell>
                                            <Group gap="s" justify="space-between" alignItems="center">
                                                <EllipsisTextHint>
                                                    <PossibleValues possibleValues={attribute.possibleValues}/>
                                                </EllipsisTextHint>
                                                <Item className="pr-xxs" style={{width: '1rem'}}>
                                                    <CopyButton textToCopy={
                                                        getPossibleValuesOrDefault(attribute.possibleValues)
                                                    }
                                                    />
                                                </Item>
                                            </Group>
                                        </Cell>
                                        <Cell verticalAlign="middle">
                                            <Group justify="center">
                                                <CheckBox
                                                    checked={attribute.deprecated}
                                                    disabled={true}
                                                />
                                            </Group>
                                        </Cell>
                                        <Cell verticalAlign="middle">
                                            <Group justify="center">
                                                {attribute.lastUsageDate
                                                    ? formatDate(attribute.lastUsageDate)
                                                    : NO_ATTRIBUTE_USAGE}
                                            </Group>
                                        </Cell>
                                    </Row>
                                ))}
                            </Body>
                        </Table>
                        {(isVisible && hasMore) ? <Loading fast={true}/> : null}
                    </Item>
                </Group>
            )}
        </>
    );
};
